import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { AlertCircle, AlertTriangle, TrendingDown, Download, Search, ChevronDown, ChevronRight, Package, Settings, CheckCircle, Archive, RotateCcw, XCircle, RefreshCw, Info } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReorderPage() {
  const [recommendations, setRecommendations] = useState([]);
  const [reorderInfo, setReorderInfo] = useState(null);
  const [stockUpdateDates, setStockUpdateDates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showOkPriority, setShowOkPriority] = useState(false);
  const [reorderSettings, setReorderSettings] = useState({ 
    extra_buffer_days: 0, 
    fba_delivery_buffer: 25,
    last_30_days_weight: 0.60,
    prev_60_days_weight: 0.40,
    discontinue_velocity_threshold: 0.2,
    discontinue_review_days: 120,
    reorder_method: 'standard'
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [switchingMethod, setSwitchingMethod] = useState(false);
  const { toast } = useToast();
  
  // Tab state - "reorder" or "discontinued"
  const [activeTab, setActiveTab] = useState('reorder');
  
  // Discontinued state
  const [discontinuedSuggestions, setDiscontinuedSuggestions] = useState([]);
  const [discontinuedLoading, setDiscontinuedLoading] = useState(false);
  const [confirmedDiscontinuedSkus, setConfirmedDiscontinuedSkus] = useState([]);
  const [confirmingItem, setConfirmingItem] = useState(null);
  const [reversingItem, setReversingItem] = useState(null);
  const [discontinuedSearchTerm, setDiscontinuedSearchTerm] = useState('');

  // Filter confirmed discontinued from reorder list
  const filteredRecommendations = useMemo(() => {
    let filtered = recommendations;
    const searchLower = (searchTerm || '').toLowerCase().trim();
    
    // Filter out confirmed discontinued items
    if (confirmedDiscontinuedSkus.length > 0) {
      filtered = filtered.filter(r => {
        // Check if any SKU in this group is confirmed discontinued
        const hasConfirmedDiscontinued = r.skus.some(s => confirmedDiscontinuedSkus.includes(s.sku));
        return !hasConfirmedDiscontinued;
      });
    }
    
    // Filter out "ok" priority unless toggle is on
    if (!showOkPriority) {
      filtered = filtered.filter(r => r.priority !== 'ok');
    }
    
    // Apply search filter
    if (searchLower) {
      filtered = filtered.filter(r => {
        const colorParentMatch = r.color_parent.toLowerCase().includes(searchLower);
        const vendorMatch = r.vendor.toLowerCase().includes(searchLower);
        const skuMatch = r.skus.some(s => 
          s.sku.toLowerCase().includes(searchLower) || 
          s.product_name.toLowerCase().includes(searchLower)
        );
        return colorParentMatch || vendorMatch || skuMatch;
      });
    }
    
    // Sort: by priority first, then by reorder quantity (highest first)
    const priorityOrder = { high: 0, medium: 1, low: 2, ok: 3 };
    return [...filtered].sort((a, b) => {
      // Exact search matches come first
      if (searchLower) {
        const aExact = a.color_parent.toLowerCase() === searchLower;
        const bExact = b.color_parent.toLowerCase() === searchLower;
        
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
      }
      
      // Sort by priority (high=0, medium=1, low=2, ok=3)
      const aPriority = priorityOrder[a.priority] ?? 4;
      const bPriority = priorityOrder[b.priority] ?? 4;
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      // Within same priority, sort by total_recommended_qty (highest first)
      return (b.total_recommended_qty || 0) - (a.total_recommended_qty || 0);
    });
  }, [recommendations, searchTerm, showOkPriority, confirmedDiscontinuedSkus]);

  // Filter discontinued suggestions
  const filteredDiscontinued = useMemo(() => {
    const searchLower = (discontinuedSearchTerm || '').toLowerCase().trim();
    if (!searchLower) return discontinuedSuggestions;
    
    return discontinuedSuggestions.filter(d => {
      const colorParentMatch = d.color_parent.toLowerCase().includes(searchLower);
      const skuMatch = d.skus.some(s => s.toLowerCase().includes(searchLower));
      return colorParentMatch || skuMatch;
    });
  }, [discontinuedSuggestions, discontinuedSearchTerm]);

  // Count by status
  const suggestedCount = discontinuedSuggestions.filter(d => d.status === 'suggested').length;
  const confirmedCount = discontinuedSuggestions.filter(d => d.status === 'confirmed').length;

  useEffect(() => {
    fetchRecommendations();
    fetchReorderInfo();
    fetchReorderSettings();
    fetchStockUpdateDates();
    fetchDiscontinuedData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStockUpdateDates = async () => {
    try {
      const response = await axios.get(`${API}/stock-update-dates`);
      setStockUpdateDates(response.data);
    } catch (error) {
      console.error('Failed to load stock update dates:', error);
    }
  };

  const fetchReorderSettings = async () => {
    try {
      const response = await axios.get(`${API}/reorder-settings`);
      setReorderSettings(response.data);
    } catch (error) {
      console.error('Failed to load reorder settings:', error);
    }
  };

  const fetchDiscontinuedData = async () => {
    setDiscontinuedLoading(true);
    try {
      const [suggestionsRes, skusRes] = await Promise.all([
        axios.get(`${API}/discontinued/suggestions`),
        axios.get(`${API}/discontinued/skus`)
      ]);
      setDiscontinuedSuggestions(suggestionsRes.data);
      setConfirmedDiscontinuedSkus(skusRes.data.discontinued_skus || []);
    } catch (error) {
      console.error('Failed to load discontinued data:', error);
    } finally {
      setDiscontinuedLoading(false);
    }
  };

  const saveReorderSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/reorder-settings`, reorderSettings);
      toast({
        title: 'Success',
        description: 'Reorder settings saved. Refreshing recommendations...',
      });
      setSettingsOpen(false);
      fetchRecommendations();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleMethodSwitch = async (method) => {
    setSwitchingMethod(true);
    try {
      const updated = { ...reorderSettings, reorder_method: method };
      await axios.put(`${API}/reorder-settings`, updated);
      setReorderSettings(updated);
      toast({
        title: 'Reorder Method Changed',
        description: method === 'fba_integrated' 
          ? 'Switched to FBA-Integrated mode. Loading with FBA commitments...' 
          : 'Switched to Standard mode.',
      });
      await fetchRecommendations();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to switch reorder method',
        variant: 'destructive',
      });
    } finally {
      setSwitchingMethod(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/forecast/reorder`);
      setRecommendations(response.data);
      // Auto-expand high priority groups
      const expanded = {};
      response.data.forEach((group, idx) => {
        if (group.priority === 'high' || idx < 5) {
          expanded[group.color_parent] = true;
        }
      });
      setExpandedGroups(expanded);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load reorder recommendations',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchReorderInfo = async () => {
    try {
      const response = await axios.get(`${API}/forecast/reorder/info`);
      setReorderInfo(response.data);
    } catch (error) {
      console.error('Failed to load reorder info:', error);
    }
  };

  const handleConfirmDiscontinue = async (colorParent) => {
    setConfirmingItem(colorParent);
    try {
      await axios.post(`${API}/discontinued/confirm/${encodeURIComponent(colorParent)}`);
      toast({
        title: 'Success',
        description: `"${colorParent}" marked as discontinued`,
      });
      // Refresh data
      await fetchDiscontinuedData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to confirm discontinuation',
        variant: 'destructive',
      });
    } finally {
      setConfirmingItem(null);
    }
  };

  const handleReverseDiscontinue = async (colorParent) => {
    setReversingItem(colorParent);
    try {
      await axios.post(`${API}/discontinued/reverse/${encodeURIComponent(colorParent)}`);
      toast({
        title: 'Success',
        description: `Discontinuation reversed for "${colorParent}"`,
      });
      // Refresh data
      await fetchDiscontinuedData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reverse discontinuation',
        variant: 'destructive',
      });
    } finally {
      setReversingItem(null);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/export/reorder`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reorder_plan_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: 'Success',
        description: 'Reorder plan downloaded successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download reorder plan',
        variant: 'destructive',
      });
    }
  };

  const toggleGroup = (colorParent) => {
    setExpandedGroups(prev => ({
      ...prev,
      [colorParent]: !prev[colorParent]
    }));
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'high') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-destructive/10 text-destructive text-xs font-medium" data-testid={`priority-${priority}`}>
          <AlertCircle className="w-3 h-3" strokeWidth={2} />
          HIGH
        </span>
      );
    }
    if (priority === 'medium') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-warning/10 text-warning text-xs font-medium" data-testid={`priority-${priority}`}>
          <AlertTriangle className="w-3 h-3" strokeWidth={2} />
          MEDIUM
        </span>
      );
    }
    if (priority === 'ok') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-green-500/10 text-green-600 text-xs font-medium" data-testid={`priority-${priority}`}>
          <CheckCircle className="w-3 h-3" strokeWidth={2} />
          OK
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-muted text-muted-foreground text-xs font-medium" data-testid={`priority-${priority}`}>
        <TrendingDown className="w-3 h-3" strokeWidth={2} />
        LOW
      </span>
    );
  };

  // Check if a group has suggested discontinuation (for badge display in reorder list)
  const getSuggestedDiscontinueStatus = (colorParent) => {
    const suggestion = discontinuedSuggestions.find(d => d.color_parent === colorParent);
    if (!suggestion) return null;
    return suggestion.status;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading recommendations...</p>
        </div>
      </Layout>
    );
  }

  const highPriority = recommendations.filter((r) => r.priority === 'high');
  const totalOrderValue = recommendations.reduce((sum, r) => sum + r.total_order_value, 0);
  const totalColorParents = recommendations.length;
  const totalSkus = recommendations.reduce((sum, r) => sum + r.skus.length, 0);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight font-heading" data-testid="page-title">Reorder Recommendations</h1>
            <p className="text-muted-foreground mt-2">
              Grouped by color parent with vendor-specific MOQ rules
            </p>
          </div>
          
          {recommendations.length > 0 && activeTab === 'reorder' && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="reorder-settings-button">
                <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Settings
              </Button>
              <Button onClick={handleExport} data-testid="export-reorder-button">
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Export Plan
              </Button>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('reorder')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'reorder' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
            }`}
            data-testid="tab-reorder"
          >
            <Package className="w-4 h-4 inline mr-2" />
            Reorder Plan
          </button>
          <button
            onClick={() => setActiveTab('discontinued')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'discontinued' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
            }`}
            data-testid="tab-discontinued"
          >
            <Archive className="w-4 h-4" />
            Discontinued
            {suggestedCount > 0 && (
              <span className="bg-amber-500/20 text-amber-600 text-xs px-1.5 py-0.5 rounded">
                {suggestedCount} new
              </span>
            )}
            {confirmedCount > 0 && (
              <span className="bg-red-500/20 text-red-600 text-xs px-1.5 py-0.5 rounded">
                {confirmedCount} confirmed
              </span>
            )}
          </button>
        </div>

        {/* REORDER TAB CONTENT */}
        {activeTab === 'reorder' && (
          <>
            {/* Reorder Method Toggle */}
            <div className="flex items-center justify-between border border-border rounded-sm p-3 bg-card" data-testid="reorder-method-toggle">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">Reorder Method:</span>
                <div className="flex border border-border rounded-sm overflow-hidden">
                  <button
                    onClick={() => reorderSettings.reorder_method !== 'standard' && handleMethodSwitch('standard')}
                    disabled={switchingMethod}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      reorderSettings.reorder_method === 'standard'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground hover:bg-muted/50'
                    }`}
                    data-testid="method-standard-btn"
                  >
                    Standard
                  </button>
                  <button
                    onClick={() => reorderSettings.reorder_method !== 'fba_integrated' && handleMethodSwitch('fba_integrated')}
                    disabled={switchingMethod}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      reorderSettings.reorder_method === 'fba_integrated'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground hover:bg-muted/50'
                    }`}
                    data-testid="method-fba-integrated-btn"
                  >
                    {switchingMethod ? 'Switching...' : 'FBA-Integrated'}
                  </button>
                </div>
              </div>
              <span className="text-xs text-muted-foreground max-w-md text-right">
                {reorderSettings.reorder_method === 'fba_integrated'
                  ? 'Deducts FBA/Noon committed stock. Formula: Velocity × (Lead Time + Safety Stock + Review Buffer)'
                  : 'Standard formula: Velocity × (Lead Time + Safety + Review + FBA Buffer + Extra Buffer)'}
              </span>
            </div>

            {/* Data Freshness Warning for FBA-Integrated Mode */}
            {reorderSettings.reorder_method === 'fba_integrated' && (
              <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-4" data-testid="data-freshness-warning">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-700 mb-2">
                      FBA-Integrated Mode — Ensure all data is up to date
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Sales data (all platforms)</span>
                        {stockUpdateDates?.sales_uploads && Object.keys(stockUpdateDates.sales_uploads).length > 0 ? (
                          <span className="text-green-600 font-medium">
                            {Object.keys(stockUpdateDates.sales_uploads).length} platforms
                          </span>
                        ) : (
                          <span className="text-red-500 font-medium">No data</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Central warehouse stock</span>
                        {stockUpdateDates?.central_warehouse ? (
                          <span className="text-green-600 font-medium">
                            {new Date(stockUpdateDates.central_warehouse).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        ) : (
                          <span className="text-red-500 font-medium">Not uploaded</span>
                        )}
                      </div>
                      {['amazon_uae', 'amazon_ksa', 'amazon_au', 'noon_uae'].map(platform => {
                        const label = {amazon_uae:'UAE FBA',amazon_ksa:'KSA FBA',amazon_au:'AU FBA',noon_uae:'Noon UAE'}[platform];
                        const updated = stockUpdateDates?.fba_warehouses?.[platform];
                        return (
                          <div key={platform} className="flex items-center justify-between">
                            <span className="text-muted-foreground">{label} stock & transit</span>
                            {updated ? (
                              <span className="text-green-600 font-medium">
                                {new Date(updated).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">SKU mappings</span>
                        {stockUpdateDates?.sku_mappings ? (
                          <span className="text-green-600 font-medium">Configured</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Search Bar and Filters */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  data-testid="reorder-search-input"
                  placeholder="Search by color parent, SKU, or vendor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Show OK Priority Toggle */}
              <label className="flex items-center gap-2 cursor-pointer select-none border border-border rounded-sm px-3 py-2 hover:bg-muted/30 transition-colors">
                <input
                  type="checkbox"
                  checked={showOkPriority}
                  onChange={(e) => setShowOkPriority(e.target.checked)}
                  className="w-4 h-4 rounded border-border accent-primary"
                  data-testid="show-ok-priority-toggle"
                />
                <span className="text-sm flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  Show OK Status ({recommendations.filter(r => r.priority === 'ok').length})
                </span>
              </label>
              
              <span className="text-sm text-muted-foreground">
                Showing {filteredRecommendations.length} of {recommendations.length} groups
              </span>
            </div>

            {reorderInfo && (
              <div className={`border rounded-sm p-4 ${reorderInfo.has_data ? 'bg-muted/30 border-border' : 'bg-warning/10 border-warning/30'}`} data-testid="reorder-info">
                <p className="text-sm">
                  <span className="font-medium">{reorderInfo.has_data ? 'ℹ️ Data Source: ' : '⚠️ Warning: '}</span>
                  {reorderInfo.message}
                </p>
                {reorderInfo.warning && (
                  <p className="text-sm text-warning mt-2">
                    <span className="font-medium">⚠️ {reorderInfo.warning}</span>
                  </p>
                )}
              </div>
            )}

            {/* Stock Update Info */}
            {stockUpdateDates && stockUpdateDates.central_warehouse && (
              <div className="border rounded-sm p-3 bg-blue-50 border-blue-200" data-testid="stock-update-info">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">📦 Central Warehouse Stock Updated:</span>{' '}
                  {new Date(stockUpdateDates.central_warehouse).toLocaleDateString('en-IN', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            )}

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="border border-border bg-card rounded-sm p-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Color Parents</p>
                <p className="text-2xl font-bold font-mono" data-testid="total-color-parents">{totalColorParents}</p>
              </div>
              <div className="border border-border bg-card rounded-sm p-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Total SKUs</p>
                <p className="text-2xl font-bold font-mono" data-testid="total-skus">{totalSkus}</p>
              </div>
              <div className="border border-border bg-card rounded-sm p-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">High Priority</p>
                <p className="text-2xl font-bold font-mono text-destructive" data-testid="high-priority-count">{highPriority.length}</p>
              </div>
              <div className="border border-border bg-card rounded-sm p-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Total Order Value</p>
                <p className="text-2xl font-bold font-mono" data-testid="total-order-value">₹{totalOrderValue.toLocaleString()}</p>
              </div>
            </div>

            {/* Grouped Recommendations */}
            <div className="space-y-3">
              {filteredRecommendations.length > 0 ? (
                filteredRecommendations.map((group) => {
                  const discontinueStatus = getSuggestedDiscontinueStatus(group.color_parent);
                  
                  return (
                    <div 
                      key={`${group.vendor}-${group.color_parent}`} 
                      className={`border rounded-sm overflow-hidden ${
                        !group.moq_fulfilled ? 'border-orange-500/50' : 'border-border'
                      }`}
                      data-testid={`reorder-group-${group.color_parent}`}
                    >
                      {/* Group Header */}
                      <div 
                        className={`flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors ${
                          !group.moq_fulfilled ? 'bg-orange-500/5' : 'bg-card'
                        }`}
                        onClick={() => toggleGroup(group.color_parent)}
                      >
                        <div className="flex items-center gap-4">
                          <button className="p-1">
                            {expandedGroups[group.color_parent] ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                          <div>
                            <div className="flex items-center gap-2">
                              <Package className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{group.color_parent}</span>
                              <span className="text-sm text-muted-foreground">({group.skus.length} SKUs)</span>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                              <span>Vendor: <strong>{group.vendor}</strong></span>
                              <span>Lead Time: <strong>{group.lead_time_days}d</strong></span>
                              <span>Review: <strong>{group.review_buffer_days}d</strong></span>
                              {group.moq > 0 && (
                                <span>MOQ: <strong>{group.moq}</strong></span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {/* Show suggested discontinue badge */}
                          {discontinueStatus === 'suggested' && (
                            <span className="text-xs text-amber-600 bg-amber-500/10 px-2 py-1 rounded flex items-center gap-1" data-testid={`suggested-discontinue-badge-${group.color_parent}`}>
                              <TrendingDown className="w-3 h-3" />
                              Suggested Discontinue
                            </span>
                          )}
                          {group.consider_discontinuing && !discontinueStatus && (
                            <span className="text-xs text-amber-600 bg-amber-500/10 px-2 py-1 rounded flex items-center gap-1" data-testid={`discontinue-badge-${group.color_parent}`}>
                              <TrendingDown className="w-3 h-3" />
                              Consider Discontinuing
                            </span>
                          )}
                          {!group.moq_fulfilled && group.moq > 0 && (
                            <span className="text-xs text-orange-600 bg-orange-500/10 px-2 py-1 rounded">
                              {group.moq_message || 'MOQ not met'}
                            </span>
                          )}
                          {group.sixty_percent_rule && (
                            <span className="text-xs text-blue-600 bg-blue-500/10 px-2 py-1 rounded" data-testid={`sixty-pct-rule-badge-${group.color_parent}`} title="60% of sizes are low in stock — MOQ distributed proportionally across all sizes by velocity">
                              60% Rule
                            </span>
                          )}
                          <div className="text-right">
                            <div className="font-mono font-bold">{group.total_recommended_qty} units</div>
                            <div className="text-sm text-muted-foreground">₹{group.total_order_value.toLocaleString()}</div>
                          </div>
                          {getPriorityBadge(group.priority)}
                        </div>
                      </div>

                      {/* SKU Details (Expandable) */}
                      {expandedGroups[group.color_parent] && (
                        <div className="border-t border-border overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-muted/30">
                              <tr>
                                <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">SKU</th>
                                <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">Product</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Stock</th>
                                {reorderSettings.reorder_method === 'fba_integrated' && (
                                  <>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest font-medium" title="Units committed to FBA/Noon channels">FBA Comm.</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest font-medium" title="Stock - FBA Committed">Available</th>
                                  </>
                                )}
                                <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Velocity</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Days Left</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">ROP</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Order Qty</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Max (90d)</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.skus.map((sku) => (
                                <tr 
                                  key={sku.sku} 
                                  className={`border-b border-border hover:bg-muted/20 ${
                                    sku.capped ? 'bg-yellow-500/5' : 
                                    sku.recommended_qty === 0 ? 'bg-muted/30 opacity-60' : ''
                                  }`}
                                >
                                  <td className="p-3 font-mono text-sm">
                                    <div className="flex items-center gap-2">
                                      {sku.sku}
                                      {sku.recommended_qty === 0 && (
                                        <span className="text-xs text-green-600" title="Has enough stock">✓</span>
                                      )}
                                      {sku.consider_discontinuing && (
                                        <span className="text-xs text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded" title="Consider Discontinuing">
                                          <TrendingDown className="w-3 h-3 inline" />
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-3 text-sm max-w-[200px] truncate">{sku.product_name}</td>
                                  <td className="p-3 text-right font-mono text-sm">
                                    <span className={sku.current_stock === 0 ? 'text-destructive font-medium' : ''}>
                                      {sku.current_stock}
                                    </span>
                                  </td>
                                  {reorderSettings.reorder_method === 'fba_integrated' && (
                                    <>
                                      <td className="p-3 text-right font-mono text-sm">
                                        <span className={sku.fba_committed > 0 ? 'text-blue-600 font-medium' : 'text-muted-foreground'}>
                                          {sku.fba_committed}
                                        </span>
                                      </td>
                                      <td className="p-3 text-right font-mono text-sm">
                                        <span className={sku.available_stock < 0 ? 'text-destructive font-bold' : sku.available_stock === 0 ? 'text-destructive font-medium' : ''}>
                                          {sku.available_stock}
                                        </span>
                                      </td>
                                    </>
                                  )}
                                  <td className="p-3 text-right font-mono text-sm">{sku.sales_velocity}</td>
                                  <td className="p-3 text-right font-mono text-sm">
                                    <span className={sku.days_of_stock < 14 ? 'text-destructive font-medium' : ''}>
                                      {sku.days_of_stock}
                                    </span>
                                  </td>
                                  <td className="p-3 text-right font-mono text-sm">{sku.reorder_point}</td>
                                  <td className="p-3 text-right font-mono text-sm font-medium">
                                    {sku.recommended_qty}
                                    {sku.capped && (
                                      <span className="ml-1 text-xs text-yellow-600" title="Capped at 90-day ceiling">⚠️</span>
                                    )}
                                  </td>
                                  <td className="p-3 text-right font-mono text-sm text-muted-foreground">{sku.max_qty_90_days}</td>
                                  <td className="p-3 text-right font-mono text-sm">₹{sku.order_value.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="border border-border rounded-sm p-12 text-center">
                  <p className="text-muted-foreground" data-testid="no-reorder-message">
                    {searchTerm 
                      ? `No groups found matching "${searchTerm}"`
                      : 'No reorder recommendations at this time.'
                    }
                  </p>
                  {!searchTerm && (
                    <p className="text-sm text-muted-foreground mt-2">
                      All products are above their reorder points.
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* DISCONTINUED TAB CONTENT */}
        {activeTab === 'discontinued' && (
          <div className="space-y-4">
            {/* Info Banner */}
            <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-4">
              <p className="text-sm">
                <span className="font-medium text-amber-700">📋 Discontinuation Criteria:</span>{' '}
                <span className="text-muted-foreground">
                  Color parents with ≥40% of FBA/Noon stock aging 90+ days AND sales velocity &lt;0.2 over 120 days.
                </span>
              </p>
            </div>

            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  data-testid="discontinued-search-input"
                  placeholder="Search by color parent or SKU..."
                  value={discontinuedSearchTerm}
                  onChange={(e) => setDiscontinuedSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <span className="text-sm text-muted-foreground">
                {filteredDiscontinued.length} items ({suggestedCount} suggested, {confirmedCount} confirmed)
              </span>
            </div>

            {/* Discontinued List */}
            {discontinuedLoading ? (
              <div className="text-center py-12 text-muted-foreground">
                Loading discontinued suggestions...
              </div>
            ) : filteredDiscontinued.length > 0 ? (
              <div className="space-y-3">
                {filteredDiscontinued.map((item) => (
                  <div 
                    key={item.color_parent}
                    className={`border rounded-sm p-4 ${
                      item.status === 'confirmed' 
                        ? 'border-red-500/30 bg-red-500/5' 
                        : 'border-amber-500/30 bg-amber-500/5'
                    }`}
                    data-testid={`discontinued-item-${item.color_parent}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-medium text-lg">{item.color_parent}</span>
                          {item.status === 'confirmed' ? (
                            <span className="text-xs bg-red-500/20 text-red-600 px-2 py-0.5 rounded flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              CONFIRMED DISCONTINUED
                            </span>
                          ) : (
                            <span className="text-xs bg-amber-500/20 text-amber-600 px-2 py-0.5 rounded flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              SUGGESTED
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                          <div>
                            <span className="text-muted-foreground">Total Stock:</span>{' '}
                            <span className="font-mono font-medium">{item.total_stock}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Aged 90+ Days:</span>{' '}
                            <span className="font-mono font-medium text-amber-600">{item.aged_stock} ({item.aged_percentage}%)</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Velocity (120d):</span>{' '}
                            <span className="font-mono font-medium text-red-600">{item.sales_velocity_120d.toFixed(3)}/day</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">SKUs:</span>{' '}
                            <span className="font-mono">{item.sku_count}</span>
                          </div>
                        </div>
                        
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium">SKUs:</span>{' '}
                          {item.skus.slice(0, 5).join(', ')}
                          {item.skus.length > 5 && ` +${item.skus.length - 5} more`}
                        </div>
                        
                        {item.status === 'confirmed' && item.confirmed_at && (
                          <div className="text-xs text-muted-foreground mt-2">
                            Confirmed by {item.confirmed_by} on {new Date(item.confirmed_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        {item.status === 'suggested' ? (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleConfirmDiscontinue(item.color_parent)}
                            disabled={confirmingItem === item.color_parent}
                            data-testid={`confirm-discontinue-${item.color_parent}`}
                          >
                            {confirmingItem === item.color_parent ? (
                              'Confirming...'
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Confirm
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReverseDiscontinue(item.color_parent)}
                            disabled={reversingItem === item.color_parent}
                            data-testid={`reverse-discontinue-${item.color_parent}`}
                          >
                            {reversingItem === item.color_parent ? (
                              'Reversing...'
                            ) : (
                              <>
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Reverse
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-border rounded-sm p-12 text-center">
                <Archive className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground" data-testid="no-discontinued-message">
                  {discontinuedSearchTerm
                    ? `No discontinued items matching "${discontinuedSearchTerm}"`
                    : 'No products meet the discontinuation criteria.'
                  }
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Products must have ≥40% aged stock (90+ days) AND velocity &lt;0.2/day to appear here.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Reorder Settings Dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reorder Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-muted/30 border border-border rounded-sm p-4 text-sm">
                <p className="font-medium mb-2">Velocity Formula:</p>
                <p className="text-muted-foreground">
                  Weighted Velocity = (Last 30 days × Weight₁) + (Previous 60 days × Weight₂)
                </p>
                <p className="font-medium mt-3 mb-2">Reorder Point ({reorderSettings.reorder_method === 'fba_integrated' ? 'FBA-Integrated' : 'Standard'}):</p>
                <p className="text-muted-foreground">
                  {reorderSettings.reorder_method === 'fba_integrated'
                    ? 'Velocity × (Lead Time + Safety Stock + Review Buffer)'
                    : 'Velocity × (Lead Time + Safety Stock + Review Buffer + FBA Buffer + Extra Buffer)'}
                </p>
                {reorderSettings.reorder_method === 'fba_integrated' && (
                  <p className="text-xs text-blue-600 mt-2">
                    FBA Buffer and Extra Buffer are excluded — FBA committed stock is deducted from available stock instead.
                  </p>
                )}
              </div>

              <div className="border border-border rounded-sm p-3">
                <p className="text-sm font-medium mb-3">Sales Velocity Weights</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Last 30 Days Weight</label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={reorderSettings.last_30_days_weight}
                      onChange={(e) => setReorderSettings(prev => ({...prev, last_30_days_weight: parseFloat(e.target.value) || 0}))}
                      data-testid="last-30-weight-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Previous 60 Days Weight</label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={reorderSettings.prev_60_days_weight}
                      onChange={(e) => setReorderSettings(prev => ({...prev, prev_60_days_weight: parseFloat(e.target.value) || 0}))}
                      data-testid="prev-60-weight-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Total should equal 1.0 (Current: {((reorderSettings.last_30_days_weight || 0) + (reorderSettings.prev_60_days_weight || 0)).toFixed(2)})
                </p>
              </div>

              {/* FBA Buffer & Extra Buffer — only shown in Standard mode */}
              {reorderSettings.reorder_method !== 'fba_integrated' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      FBA Delivery Buffer (days)
                      <span className="text-muted-foreground font-normal ml-2">Central → FBA/Noon</span>
                    </label>
                    <Input
                      type="number"
                      value={reorderSettings.fba_delivery_buffer}
                      onChange={(e) => setReorderSettings(prev => ({...prev, fba_delivery_buffer: parseInt(e.target.value) || 0}))}
                      data-testid="fba-buffer-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Extra Buffer (days)
                      <span className="text-muted-foreground font-normal ml-2">Additional safety margin</span>
                    </label>
                    <Input
                      type="number"
                      value={reorderSettings.extra_buffer_days}
                      onChange={(e) => setReorderSettings(prev => ({...prev, extra_buffer_days: parseInt(e.target.value) || 0}))}
                      data-testid="extra-buffer-input"
                    />
                  </div>
                </>
              )}

              <div className="border border-border rounded-sm p-3">
                <p className="text-sm font-medium mb-3 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-amber-600" />
                  Discontinue Suggestions
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Velocity Threshold (units/day)</label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      value={reorderSettings.discontinue_velocity_threshold}
                      onChange={(e) => setReorderSettings(prev => ({...prev, discontinue_velocity_threshold: parseFloat(e.target.value) || 0}))}
                      data-testid="discontinue-threshold-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Review Period (days)</label>
                    <Input
                      type="number"
                      min="1"
                      value={reorderSettings.discontinue_review_days}
                      onChange={(e) => setReorderSettings(prev => ({...prev, discontinue_review_days: parseInt(e.target.value) || 120}))}
                      data-testid="discontinue-days-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Products with color parent velocity &lt; {reorderSettings.discontinue_velocity_threshold || 0.2}/day over {reorderSettings.discontinue_review_days || 120} days will be flagged.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={saveReorderSettings} disabled={savingSettings} className="flex-1">
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </Button>
                <Button variant="outline" onClick={() => setSettingsOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
